import React, { useState } from "react";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import ScrollReveal from "@/components/ui/scroll-reveal";
import { Calculator, TrendingUp, Clock, Target, ArrowRight, Sparkles } from "lucide-react";
import { useLanguage } from "@/contexts/LanguageContext";
import ROIWizard from "./ROIWizard";

const ROICalculator = () => {
  const { language } = useLanguage();
  const [wizardOpen, setWizardOpen] = useState(false);

  const tr = {
    badge: language === 'sv' ? 'ROI-kalkylator' : 'ROI Calculator',
    title: language === 'sv' ? 'Beräkna dina besparingar på bara 2 minuter' : 'Calculate Your Savings in Just 2 Minutes',
    subtitle: language === 'sv' 
      ? 'Se exakt hur mycket intäkter du förlorar och hur snabbt HostCall.ai betalar sig själv' 
      : 'See exactly how much revenue you\'re losing and how quickly HostCall.ai pays for itself',
    avgSavings: language === 'sv' ? 'Snittbesparing/år' : 'Avg. Annual Savings',
    avgPayback: language === 'sv' ? 'Snitt återbetalningstid' : 'Avg. Payback Time',
    avgROI: language === 'sv' ? 'Snitt År 1 ROI' : 'Avg. Year 1 ROI',
    cta: language === 'sv' ? 'Starta ROI-beräkning' : 'Start Your ROI Calculation',
    noEmail: language === 'sv' ? 'Ingen e-post krävs. Se resultat direkt.' : 'No email required. See results instantly.'
  };

  const Stat = ({ icon: Icon, value, label }: { icon: React.ElementType; value: string; label: string }) => (
    <div className="flex flex-col items-center text-center">
      <div className="p-2 bg-white/20 rounded-lg mb-2">
        <Icon className="h-5 w-5 text-brand-600" />
      </div>
      <p className="text-2xl md:text-3xl font-bold text-brand-700">{value}</p>
      <p className="text-xs text-brand-600/80">{label}</p>
    </div>
  );

  return (
    <section id="roi-calculator" className="section-padding bg-gradient-to-b from-muted/30 to-background relative overflow-hidden">
      {/* Background decoration */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        <div className="absolute -top-40 -right-40 w-80 h-80 bg-brand-200/30 dark:bg-brand-800/20 rounded-full blur-3xl" />
        <div className="absolute -bottom-40 -left-40 w-80 h-80 bg-pink-200/30 dark:bg-pink-800/20 rounded-full blur-3xl" />
      </div>
      
      <div className="container mx-auto px-4 relative z-10">
        <ScrollReveal>
          <div className="max-w-3xl mx-auto text-center">
            <Badge className="mb-4 bg-brand-100 text-brand-700 dark:bg-brand-900/40 dark:text-brand-300 hover:bg-brand-100">
              <Calculator className="h-4 w-4 mr-2" />
              {tr.badge}
            </Badge>
            
            <h2 className="text-3xl md:text-4xl lg:text-5xl font-bold mb-4 text-foreground">
              {tr.title}
            </h2>
            
            <p className="text-lg text-muted-foreground mb-8">
              {tr.subtitle}
            </p>
            
            <Card className="p-8 bg-gradient-to-br from-brand-50 via-white to-pink-50 dark:from-brand-950/50 dark:via-background dark:to-pink-950/30 border-brand-200/50 dark:border-brand-800/30 shadow-xl">
              <div className="flex items-center justify-center gap-6 md:gap-12 mb-8 flex-wrap">
                <Stat icon={TrendingUp} value="$109K+" label={tr.avgSavings} />
                <Stat icon={Clock} value="<3 mo" label={tr.avgPayback} />
                <Stat icon={Target} value="485%" label={tr.avgROI} />
              </div>
              
              <Button 
                size="lg" 
                onClick={() => setWizardOpen(true)}
                className="text-lg px-8 py-6 group bg-gradient-to-r from-brand-600 to-brand-500 hover:from-brand-700 hover:to-brand-600 shadow-lg hover:shadow-xl transition-all"
              >
                <Sparkles className="mr-2 h-5 w-5" />
                {tr.cta}
                <ArrowRight className="ml-2 h-5 w-5 group-hover:translate-x-1 transition-transform" />
              </Button>
              
              <p className="text-sm text-muted-foreground mt-4">
                {tr.noEmail}
              </p>
            </Card>
          </div>
        </ScrollReveal>
      </div>
      
      <ROIWizard open={wizardOpen} onOpenChange={setWizardOpen} />
    </section>
  );
};

export default ROICalculator;
